import { createResource } from "~/lib/resourceFactory";
import { getPnL } from "./p-and-l.api";

export type PnLParams = {
  businessId: string;
  from?: string;
  to?: string;
  classIds?: string[];
};

export type PnLData = Awaited<ReturnType<typeof getPnL>>;

function toIsoDate(d: Date): string {
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  return `${d.getFullYear()}-${mm}-${dd}`;
}

/** First day of the current calendar year (ISO date). */
export function startOfYearIso(): string {
  const now = new Date();
  return `${now.getFullYear()}-01-01`;
}

/** Today's date as an ISO string. */
export function todayIso(): string {
  return toIsoDate(new Date());
}

/**
 * The default params the P&L report renders with: current calendar year-to-date,
 * all classes. The route loader seeds the query cache with these exact params so
 * the component's createQuery hook finds data on the first paint (no "pop in").
 */
export function defaultPnLParams(businessId: string): PnLParams {
  return { businessId, from: startOfYearIso(), to: todayIso(), classIds: [] };
}

export const PnLResource = createResource<PnLData, PnLParams>({
  key: (p) =>
    [
      "reports",
      "pnl",
      p.businessId,
      p.from ?? "*",
      p.to ?? "*",
      ...(p.classIds ?? []).sort(),
    ] as const,
  fetcher: (p) =>
    getPnL({
      data: {
        businessId: p.businessId,
        from: p.from,
        to: p.to,
        classIds: p.classIds,
      },
    }),
  // Avoid suspending SSR when the query is disabled (no active business).
  placeholderData: {
    from: "",
    to: "",
    comparisonFrom: "",
    comparisonTo: "",
    revenue: [],
    expense: [],
    totalRevenue: 0,
    totalExpenses: 0,
    netIncome: 0,
    comparisonTotalRevenue: 0,
    comparisonTotalExpenses: 0,
    comparisonNetIncome: 0,
  },
});
